import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

const formatDateString = (dateStr) => {
    const date = new Date(dateStr);
    const options = { day: 'numeric', month: 'short', weekday: 'short' };
    const formatted = date.toLocaleDateString('en-US', options);
    const day = date.getDate();
    let suffix = 'th';
    if (day === 1 || day === 21 || day === 31) suffix = 'st';
    else if (day === 2 || day === 22) suffix = 'nd';
    else if (day === 3 || day === 23) suffix = 'rd';
    
    return formatted.replace(/(\d+)/, `$1${suffix}`);
  };
  
  const formatHourLabel = (hour) => {
    if (hour === 0) return '12am-1am';
    if (hour === 12) return '12pm-1pm';
    if (hour === 24) return '12am-1am';
    
    const period = hour < 12 ? 'am' : 'pm';
    const displayHour = hour > 12 ? hour - 12 : hour;
    const nextDisplayHour = (hour + 1) > 12 ? (hour + 1) - 12 : hour + 1;
    const nextPeriod = (hour + 1) < 12 ? 'am' : 'pm';
    
    if (hour === 23) return '11pm-12am';
    
    return `${displayHour}${period}-${nextDisplayHour}${nextPeriod}`;
  };
  
  const processDataForChart = (tableSummary) => {
    const dateHourMap = {};
    
    tableSummary.forEach(order => {
      const date = order.Date;
      const formattedDate = formatDateString(date);
      const endTime = order.End_Time;
      const [hours, minutes] = endTime.split(':').map(Number);
      
      const hourBucket = minutes > 0 ? hours + 1 : hours;
      const hourLabel = formatHourLabel(hourBucket - 1); 
      
      if (!dateHourMap[formattedDate]) {
        dateHourMap[formattedDate] = {};
      }
      
      if (!dateHourMap[formattedDate][hourLabel]) {
        dateHourMap[formattedDate][hourLabel] = 0;
      }
      
      dateHourMap[formattedDate][hourLabel] += parseFloat(order.Total);
    });
    
    const allHourLabels = new Set();
    Object.values(dateHourMap).forEach(dateData => {
      Object.keys(dateData).forEach(hour => allHourLabels.add(hour));
    });
    
    const sortedHourLabels = Array.from(allHourLabels).sort((a, b) => {
      const getHourValue = (label) => {
        if (label === '12am-1am') return 0;
        if (label.startsWith('12pm')) return 12;
        const hour = parseInt(label.split(/[ap]m/)[0]);
        return label.includes('am') ? hour : hour + 12;
      };
      return getHourValue(a) - getHourValue(b);
    });
    
    const datasets = [];
    Object.entries(dateHourMap).forEach(([date, hourData]) => {
      const data = sortedHourLabels.map(hour => hourData[hour] || 0);
      
      const colorHash = Array.from(date).reduce((hash, char) => {
        return char.charCodeAt(0) + (hash << 6) + (hash << 16) - hash;
      }, 0);
      const hue = Math.abs(colorHash) % 360;
      
      datasets.push({
        label: date,
        data: data,
        borderColor: `hsl(${hue}, 70%, 50%)`,
        backgroundColor: 'rgba(0, 0, 0, 0)',
        tension: 0.1,
        borderWidth: 2
      });
    });
    
    return {
      labels: sortedHourLabels,
      datasets: datasets
    };
  };

const DateTimeChart = ({ tableSummary }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (tableSummary && tableSummary.length > 0) {
            const chartData = processDataForChart(tableSummary);

            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            chartInstance.current = new Chart(ctx, {
                type: 'line',
                data: chartData,
                options: {
                    responsive: true,
                    plugins: {
                        title: {
                            display: true,
                            text: 'Hourly Revenue by Date',
                            font: {
                                size: 16
                            }
                        },
                        tooltip: {
                            callbacks: {
                                label: function (context) {
                                    return `${context.dataset.label}: Rs. ${context.raw.toFixed(2)}`;
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            title: {
                                display: true,
                                text: 'Time Buckets'
                            }
                        },
                        y: {
                            title: {
                                display: true,
                                text: 'Total Revenue (Rs.)'
                            },
                            beginAtZero: true
                        }
                    }
                }
            });
        }

        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [tableSummary]);

    return (
        <div style={{ width: '100%', maxWidth: '1100px', margin: '0 auto' }}>
            <canvas ref={chartRef}></canvas>
        </div>
    );
};

export default DateTimeChart;